import type { EChartsOption } from "echarts-for-react/src/types";
import type { BacktestItem } from "epsm-api-types/models";
import backtestConfig from "@/components/widgets/Backtest/config";
import { getEchartsDefaultColors } from "@/components/widgets/Backtest/helpers";
import type { IChartData } from "@/components/widgets/Backtest/types.ts";
import { formatValueCurrency } from "@/components/widgets/helpers/FormattingHelpers";

const { LIGHT_GRAY_COLOR, DARK_COLOR } = backtestConfig;

export const getDrawdownOptions = (data: BacktestItem[]): EChartsOption => {
	const echartsColors = getEchartsDefaultColors();
	const transformedData: IChartData[] = data
		.filter((item) => item.profit_and_loss !== 0)
		.map((item) => ({
			date: item.date.toISOString().split("T")[0],
			value: item.profit_and_loss ? item.amount_of_money + (item.cash || 0) : 0,
			ticker: item.ticker,
		}))
		.sort((a, b) => a.date.localeCompare(b.date));

	const tickers = Array.from(
		new Set(transformedData.map((item) => item.ticker)),
	);
	const xAxisData = Array.from(
		new Set(transformedData.map((item) => item.date)),
	);

	const calculateDrawdown = (data: IChartData[]) => {
		let prevValue = 0;
		return data.map((item) => {
			if (item.value === null) {
				return { ...item, value: 0 };
			}
			const difference = item.value - prevValue;
			prevValue = item.value;
			return { ...item, value: difference < 0 ? difference : 0 };
		});
	};

	return {
		animationDuration: 500,
		calculable: true,
		legend: {
			data: tickers,
			textStyle: {
				color: LIGHT_GRAY_COLOR,
			},
		},
		tooltip: {
			trigger: "axis",
			axisPointer: {
				type: "cross",
				snap: true,
				label: {
					backgroundColor: DARK_COLOR,
				},
			},
		},
		xAxis: {
			type: "category",
			nameLocation: "middle",
			boundaryGap: false,
			data: xAxisData,
			axisLine: {
				show: true,
				lineStyle: {
					color: LIGHT_GRAY_COLOR,
				},
			},
		},
		yAxis: {
			name: "Drawdown",
			type: "value",
			position: "left",
			axisLabel: {
				formatter: (value) => formatValueCurrency(value),
			},
			nameTextStyle: {
				color: LIGHT_GRAY_COLOR,
			},
			axisLine: {
				show: true,
				lineStyle: {
					color: LIGHT_GRAY_COLOR,
				},
			},
		},
		axisLine: {
			lineStyle: {
				color: LIGHT_GRAY_COLOR,
			},
		},
		axisTick: {
			lineStyle: {
				color: LIGHT_GRAY_COLOR,
			},
		},
		dataZoom: [
			{
				type: "inside",
			},
			{
				show: true,
				type: "slider",
				textStyle: { color: LIGHT_GRAY_COLOR },
			},
		],
		series: tickers.length
			? tickers.map((ticker, index) => ({
					type: "line",
					name: ticker,
					showSymbol: true,
					realtimeSort: true,
					connectNulls: true,
					symbol: "none",
					labelLayout: {
						moveOverlap: "shiftY",
					},
					emphasis: {
						focus: "series",
					},
					encode: {
						x: "date",
						y: "value",
						itemName: "date",
					},
					tooltip: {
						valueFormatter: (value) => formatValueCurrency(value),
					},
					lineStyle: {
						color: echartsColors[index % echartsColors.length],
					},
					areaStyle: {
						opacity: 0.2,
					},
					data: calculateDrawdown(
						transformedData.map((item) =>
							item.ticker === ticker ? item : { ...item, value: null },
						),
					),
				}))
			: [],
	};
};
